import buttonShapes from "@insite/mobius/Button/buttonShapes";
import buttonTypes from "@insite/mobius/Button/buttonTypes";
import hoverAnimations from "@insite/mobius/Button/hoverAnimations";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import {
    ButtonAnimation,
    ButtonShape,
    ButtonType,
    ButtonVariant,
    MobiusButtonAnimation,
    MobiusButtonShape,
    MobiusButtonType,
    MobiusButtonVariant,
} from "@store/Framework/Types/Button";
import { focusStyles } from "@store/Themes/common/mixins/common";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { css } from "styled-components";

export interface MobiusButtonThemeProps {
    type?: MobiusButtonType;
    shape?: MobiusButtonShape;
    animation?: MobiusButtonAnimation;
}

export interface ButtonThemeProps {
    type?: ButtonType;
    shape?: ButtonShape;
    animation?: ButtonAnimation;
}

export const buttonThemeStyles = (variant: ButtonVariant = "primary") => {
    const _variant = variant as MobiusButtonVariant;

    return css`
        ${({ theme }) => {
            const _buttonVariantProps = theme.button[_variant];

            if (_buttonVariantProps) {
                const _type = _buttonVariantProps.buttonType;
                const _shape = _buttonVariantProps.shape as MobiusButtonShape;
                const _animation = _buttonVariantProps.hoverAnimation;

                const _buttonTypeFunction = _type ? buttonTypes[_type] : null;
                const _buttonShapeFunction = _shape ? buttonShapes[_shape] : null;
                const _hoverAnimationFunction = _animation ? hoverAnimations[_animation] : null;

                const buttonProps = {
                    ..._buttonVariantProps,
                    _color: _buttonVariantProps.color,
                    _sizeVariant: _buttonVariantProps.sizeVariant,
                    _size: _buttonVariantProps.size,
                    _shape: _buttonVariantProps.shape,
                    hoverTextColor: _buttonVariantProps.hoverTextColor,
                    theme,
                };

                return css`
                    ${_buttonTypeFunction ? _buttonTypeFunction(buttonProps) : null}
                    ${_buttonShapeFunction ? _buttonShapeFunction(buttonProps) : null}
                    ${_hoverAnimationFunction ? _hoverAnimationFunction() : null}
                    ${_buttonVariantProps.css}
                `;
            }

            return null;
        }}
    `;
};

export const buttonStyles = (type: ButtonType) => {
    return css`
        ${focusStyles()}
        ${buttonDisabledStyles(type)}
        min-width: 120px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: ${transitionHoverStyles("color", "background-color", "border-color")};

        ${({ theme }) => `
            font-weight: ${theme.design.typography.weight.bold};
            line-height: ${theme.design.typography.lineHeight.small};
        `}

        /* Store Custom Code - Start */
        &:has([class*="ButtonIcon"]) {
            justify-content: space-between;
        }
        /* Store Custom Code - End */

        [class*="ButtonIcon"] {
            top: 0;
            height: 1em;
            color: inherit;

            svg {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate3d(-50%, -50%, 0);
            }
        }
    `;
};

export const buttonDisabledStyles = (type: ButtonType) => {
    switch (type) {
        case "solid":
            return css`
                &:disabled {
                    background-color: ${getColor("button.disabled")};
                    border-color: ${getColor("button.disabled")};
                    color: ${getColor("button.disabled.contrast")};
                }
            `;
        case "outline":
            return css`
                &:disabled {
                    border-color: ${getColor("button.disabled")};
                    color: ${getColor("button.disabled.contrast")};
                }
            `;
        default:
            break;
    }
};

export const buttonResetStyles = (resetHover = true) => {
    return css`
        ${focusStyles()}
        min-width: 0;
        min-height: 0;
        padding: 0;
        border: 0;
        border-radius: 0;
        background-color: transparent;
        text-transform: inherit;
        font-size: inherit;
        font-weight: inherit;
        line-height: inherit;

        ${resetHover
            ? css`
                  &:hover,
                  &:active {
                      background-color: transparent;
                  }
              `
            : null}

        ${TypographyStyle} {
            font-size: inherit;
            font-weight: inherit;
        }

        ${IconWrapper} {
            color: inherit;
        }
    `;
};

export const buttonFieldIconStyles = () => {
    return css`
        ${({ theme }) => {
            if (theme.design.button.field.enabled) {
                const scale = 1 + theme.design.button.field.scaling;
                const align = 50 * (1 - theme.design.button.field.scaling);

                return css`
                    &:not(#important) {
                        top: 50%;
                        left: 50%;
                        right: auto;
                        display: block;
                        width: auto;
                        height: auto;
                        padding: 0;
                        background-color: transparent;
                        color: inherit;
                        line-height: 0;
                        transform: scale(${scale}) translate3d(-${align}%, -${align}%, 0);
                    }
                `;
            }
        }}
    `;
};

export const buttonFieldIconClickableStyles = (variant?: ButtonVariant) => {
    return css`
        ${({ theme }) => {
            if (theme.design.button.field.enabled) {
                return css`
                    &:not(#important) {
                        ${buttonThemeStyles(variant)}
                        min-width: auto;
                        border: 0;

                        ${({ theme }) => css`
                            ${focusStyles(true)}
                            transform: scale(${1 - theme.design.button.field.scaling});
                        `}
                    }
                `;
            }
        }}
    `;
};
